// Stepping through the Spec tab's changed places (FR-EDIT-07).
//
// The server marks each line the head added or rewrote, and gives the
// first mark of each changed place its own id: `first-change`, then
// `change-2`, `change-3`, …. Its bar's **Jump to first change** leads to
// the first, and when there are more it carries how many in
// `data-change-places`. Without this script the link goes to the first
// change and stays there. With it, once a press has gone to one place the
// link names the next, **Next change (2 of 3)**, and leads there, the first
// again after the last, as the editor's bar does; each press has the
// announcer say which place it went to and where: "Change 2 of 3:
// Constraints, ~pacdet." The link itself still does the going, so the page
// lands on the place and its address names it, as without the script.

import { announce } from "./announce";

/** The id of the first mark of changed place `n`, from 1. */
export function changeId(n: number): string {
  return n <= 1 ? "first-change" : `change-${n}`;
}

/** Which changed place an id names, from 1; 0 for any other id. */
export function placeOf(id: string): number {
  if (id === "first-change") return 1;
  const n = /^change-([0-9]+)$/.exec(id)?.[1];
  return n ? Number(n) : 0;
}

/**
 * Where a mark is, as a reader finds it: the section it is under, then the
 * item it is on, "Constraints, ~pacdet", or either alone; empty when it is
 * under no section and on no item.
 */
export function placeWords(mark: Element): string {
  const article = mark.closest("article") ?? document.body;
  let section: Element | null = mark.closest("h2");
  if (!section) {
    for (const heading of article.querySelectorAll("h2")) {
      if (
        heading.compareDocumentPosition(mark) & Node.DOCUMENT_POSITION_FOLLOWING
      ) {
        section = heading;
      }
    }
  }
  // The item the mark is on: the nearest list item with an item's own
  // anchor of its own, so a check under an item names that item.
  let item: string | null = null;
  for (let li = mark.closest("li"); li && !item; ) {
    for (const anchor of li.querySelectorAll<HTMLElement>('a[id^="~"]')) {
      if (anchor.closest("li") === li) {
        item = anchor.id;
        break;
      }
    }
    li = li.parentElement?.closest("li") ?? null;
  }
  return [section?.textContent?.trim(), item].filter((part) => part).join(", ");
}

document.addEventListener("click", (event) => {
  const link = (event.target as Element | null)?.closest?.(
    "a[data-change-places]",
  );
  if (!(link instanceof HTMLAnchorElement)) return;
  const places = Number(link.dataset.changePlaces);
  if (!Number.isInteger(places) || places < 2) return;
  const at = placeOf(decodeURIComponent(link.hash.slice(1)));
  if (at < 1 || at > places) return;
  const mark = document.getElementById(changeId(at));
  const where = mark ? placeWords(mark) : "";
  announce(`Change ${at} of ${places}${where ? `: ${where}` : ""}.`);
  const next = at < places ? at + 1 : 1;
  // After the press has followed the link: changed now, it would lead
  // there instead.
  window.setTimeout(() => {
    link.setAttribute("href", `#${changeId(next)}`);
    link.textContent = `Next change (${next} of ${places})`;
  }, 0);
});
